import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Loader, Search, Users, X } from "lucide-react";
import { useDispatch } from "react-redux";
import AdminDualListBox from "./AdminDualListBox";
import ToastMessage from "../../utils/toasts/toastMessage";
import {
  createAdminCohort,
  getCohortGroups,
  updateAdminCohort,
} from "../../Redux/Admin_Panel/cohort/cohort.action";
import { truncateText } from "../../utils/utils";

const norm = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const AdminCreateCohortModal = ({
  show,
  onHide,
  students = [],
  schoolList = [],
  cohortData = null,
  editing = false,
}) => {
  const dispatch = useDispatch();
  const [cohortName, setCohortName] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [schoolDropdownOpen, setSchoolDropdownOpen] = useState(false);
  const [selectedClassDivision, setSelectedClassDivision] = useState(""); // "grade|division"
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [memberIds, setMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const dropdownRef = useRef(null);
  const schoolRef = useRef(null);
  const schoolInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setToastState({ show: false, message: "", type: "" });
    if (editing && cohortData) {
      setCohortName(cohortData.group_name || cohortData.name || "");
      setSelectedClassDivision(
        cohortData.grade && cohortData.division
          ? `${cohortData.grade}|${cohortData.division}`
          : "",
      );
      setMemberIds(cohortData.student_id || cohortData.student_ids || []);
    } else {
      setCohortName("");
      setSelectedSchool("");
      setSelectedClassDivision("");
      setMemberIds([]);
    }
    setSchoolSearch("");
    setSchoolDropdownOpen(false);
    setDropdownOpen(false);
  }, [show, editing, cohortData]);

  useEffect(() => {
    if (!dropdownOpen && !schoolDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (schoolRef.current && !schoolRef.current.contains(e.target)) {
        setSchoolDropdownOpen(false);
        setSchoolSearch("");
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setDropdownOpen(false);
        setSchoolDropdownOpen(false);
        setSchoolSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [dropdownOpen, schoolDropdownOpen]);

  const schoolNameBySchoolId = useMemo(() => {
    const map = {};
    schoolList.forEach((s) => {
      if (s?.school_id) map[s.school_id] = s.school_name;
    });
    return map;
  }, [schoolList]);

  const editSchoolName =
    editing && cohortData
      ? schoolNameBySchoolId[cohortData.school_id] ||
        cohortData.school_name ||
        cohortData.school ||
        ""
      : "";

  const activeSchool = editing ? editSchoolName : selectedSchool;
  const schoolDisplay =
    activeSchool ||
    (editing ? truncateText(schoolNameBySchoolId, 25) || "" : "");

  const schoolOptions = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      if (s?.school) set.add(s.school);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [students]);

  const filteredSchools = useMemo(() => {
    const q = schoolSearch.trim().toLowerCase();

    const uniqueSchools = [
      ...new Map(
        schoolOptions.map((school) => [
          school.trim().toLowerCase(),
          school.trim(),
        ]),
      ).values(),
    ];

    if (!q) return uniqueSchools;

    return uniqueSchools.filter((s) => s.toLowerCase().includes(q));
  }, [schoolOptions, schoolSearch]);

  const classDivisionStudentsMap = useMemo(() => {
    const map = {};
    if (!activeSchool) return map;
    const activeSchoolNorm = norm(activeSchool);
    students
      .filter((s) => norm(s.school) === activeSchoolNorm) // <-- normalized compare
      .forEach((s) => {
        const fullName = `${s.firstname || ""} ${s.lastname || ""}`.trim();
        const isTeacher = s.role_id === 2;

        (s.classes || []).forEach((c) => {
          const key = `${c.grade}|${c.division}`;
          if (!map[key]) map[key] = [];
          map[key].push({
            ...s,
            id: s.user_id,
            name: isTeacher ? `${fullName} (Teacher)` : fullName,
          });
        });
      });
    return map;
  }, [students, activeSchool]);
  const classDivisionOptions = useMemo(() => {
    return Object.keys(classDivisionStudentsMap)
      .map((key) => {
        const [grade, division] = key.split("|");
        return {
          value: key,
          label: `${grade}${division}`,
          grade,
          division,
          count: classDivisionStudentsMap[key].length,
        };
      })
      .sort(
        (a, b) =>
          a.grade.localeCompare(b.grade, undefined, { numeric: true }) ||
          a.division.localeCompare(b.division),
      );
  }, [classDivisionStudentsMap]);

  useEffect(() => {
    if (!editing || !cohortData?.grade || !cohortData?.division) return;
    const wanted = `${cohortData.grade}|${cohortData.division}`.toLowerCase();
    const matchKey = Object.keys(classDivisionStudentsMap).find(
      (k) => k.toLowerCase() === wanted,
    );
    if (matchKey && matchKey !== selectedClassDivision) {
      setSelectedClassDivision(matchKey);
    }
  }, [editing, cohortData, classDivisionStudentsMap]);

  const classStudents = useMemo(() => {
    if (!selectedClassDivision) return [];
    return classDivisionStudentsMap[selectedClassDivision] || [];
  }, [selectedClassDivision, classDivisionStudentsMap]);

  const selectSchool = (school) => {
    setSelectedSchool(school);
    setSelectedClassDivision("");
    setMemberIds([]);
    setSchoolSearch("");
    setSchoolDropdownOpen(false);
  };

  const selectClassDivision = (value) => {
    setSelectedClassDivision(value);
    setMemberIds([]);
    setDropdownOpen(false);
  };

  const showError = (message) =>
    setToastState({ show: true, message, type: "error" });

  const handleSave = async () => {
    if (!activeSchool) return showError("School is required.");
    if (!cohortName.trim()) return showError("Cohort name is required.");
    if (!selectedClassDivision) return showError("Class is required.");
    if (memberIds.length === 0) return showError("Select at least one member.");

    setSaving(true);

    try {
      let result;

      const [grade, division] = selectedClassDivision.split("|");
      const savedCohort = {
        group_name: cohortName,
        school_name: activeSchool,
        student_id: memberIds,
        grade: grade,
        division: division,
      };

      if (editing) {
        result = await dispatch(updateAdminCohort(cohortData?.id, savedCohort));
      } else {
        result = await dispatch(createAdminCohort(savedCohort));
      }

      if (result?.success === true) {
        setToastState({
          show: true,
          message:
            result?.message ||
            (editing
              ? "Cohort updated successfully."
              : "Cohort created successfully."),
          type: "success",
        });
        dispatch(getCohortGroups());
        setTimeout(() => {
          onHide?.();
        }, 500);
      } else {
        showError(
          result?.message ||
            (editing
              ? "Could not update cohort. Please try again."
              : "Could not create cohort. Please try again."),
        );
      }
    } catch {
      showError("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const selectedLabel =
    classDivisionOptions.find((o) => o.value === selectedClassDivision)
      ?.label || selectedClassDivision.replace("|", "");

  const classDisabled = editing || !activeSchool;

  return (
    <div
      className="modal fade show cohort-modal"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cm-title"
      style={{ display: "block", background: "rgba(16,24,40,0.55)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg cm-dialog">
        <div className="modal-content cm-content">
          {/* Header */}
          <div className="cm-header">
            <div>
              <h4 id="cm-title" className="cm-title">
                {editing ? "Edit Cohort" : "Create New Cohort"}
              </h4>
              <p className="cm-subtitle">
                {editing
                  ? "Update who belongs to this cohort."
                  : "Choose a school and class, then pick the members."}
              </p>
            </div>
            <button
              type="button"
              className="cm-close"
              onClick={onHide}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="cm-body">
            <div className="cm-form-grid">
              <div className="cm-field" ref={schoolRef}>
                <label className="cm-label" htmlFor="cm-school">
                  School
                </label>
                <div
                  className={`cm-control ${
                    schoolDropdownOpen ? "is-open" : ""
                  } ${editing ? "is-disabled" : ""}`}
                  onClick={() => {
                    if (editing) return;
                    setSchoolDropdownOpen(true);
                    schoolInputRef.current?.focus();
                  }}
                >
                  <Search size={16} className="cm-leading-icon" />
                  <input
                    id="cm-school"
                    ref={schoolInputRef}
                    type="text"
                    className="cm-input"
                    placeholder="Search school…"
                    value={schoolDropdownOpen ? schoolSearch : schoolDisplay}
                    onFocus={() => !editing && setSchoolDropdownOpen(true)}
                    onChange={(e) => {
                      setSchoolSearch(e.target.value);
                      setSchoolDropdownOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && filteredSchools[0]) {
                        e.preventDefault();
                        selectSchool(filteredSchools[0]);
                      }
                    }}
                    disabled={editing}
                    autoComplete="off"
                  />
                  {!editing && activeSchool && !schoolDropdownOpen && (
                    <button
                      type="button"
                      className="cm-icon-btn"
                      aria-label="Clear school"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectSchool("");
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                  <ChevronDown size={16} className="cm-chevron" />
                </div>

                {schoolDropdownOpen && (
                  <div className="cm-menu" role="listbox">
                    {filteredSchools.length === 0 && (
                      <div className="cm-empty">No schools found</div>
                    )}
                    {filteredSchools.map((school) => (
                      <button
                        type="button"
                        key={school}
                        role="option"
                        aria-selected={school === activeSchool}
                        className={`cm-option ${
                          school === activeSchool ? "is-selected" : ""
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectSchool(school);
                        }}
                      >
                        <span className="cm-option-label">{school}</span>
                        {school === activeSchool && <Check size={16} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Class */}
              <div className="cm-field" ref={dropdownRef}>
                <span className="cm-label">Class</span>
                <button
                  type="button"
                  className={`cm-control cm-select ${
                    dropdownOpen ? "is-open" : ""
                  } ${classDisabled ? "is-disabled" : ""}`}
                  onClick={() => setDropdownOpen((o) => !o)}
                  disabled={classDisabled}
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                >
                  <span
                    className={`cm-select-value ${
                      selectedClassDivision ? "" : "is-placeholder"
                    }`}
                  >
                    {selectedClassDivision
                      ? selectedLabel
                      : activeSchool
                        ? "Select class"
                        : "Select a school first"}
                  </span>
                  <ChevronDown size={16} className="cm-chevron" />
                </button>

                {dropdownOpen && (
                  <div className="cm-menu" role="listbox">
                    {classDivisionOptions.length === 0 && (
                      <div className="cm-empty">No classes available</div>
                    )}
                    {classDivisionOptions.map((option) => (
                      <button
                        type="button"
                        key={option.value}
                        role="option"
                        aria-selected={selectedClassDivision === option.value}
                        className={`cm-option ${
                          selectedClassDivision === option.value
                            ? "is-selected"
                            : ""
                        }`}
                        onClick={() => selectClassDivision(option.value)}
                      >
                        <span className="cm-option-label">{option.label}</span>
                        <span className="cm-option-meta">
                          {option.count} member{option.count === 1 ? "" : "s"}
                        </span>
                        {selectedClassDivision === option.value && (
                          <Check size={16} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cohort name */}
              <div className="cm-field">
                <label className="cm-label" htmlFor="cm-name">
                  Cohort Name
                </label>
                <div className={`cm-control ${editing ? "is-disabled" : ""}`}>
                  <input
                    id="cm-name"
                    type="text"
                    className="cm-input"
                    placeholder="e.g. Maths Advanced Batch"
                    maxLength={60}
                    value={cohortName}
                    onChange={(e) => setCohortName(e.target.value)}
                    disabled={editing}
                  />
                </div>
              </div>
            </div>

            {/* Members */}
            <div className="cm-section-head"></div>

            {selectedClassDivision ? (
              <AdminDualListBox
                allUsers={classStudents}
                currentIds={memberIds}
                onChange={setMemberIds}
                leftTitle="Current Cohort Members"
                rightTitle="Available Members"
              />
            ) : (
              <div className="cm-placeholder">
                <Users size={28} />
                <strong>
                  {activeSchool ? "Pick a class" : "Pick a school"}
                </strong>
                <span>
                  {activeSchool
                    ? "Students from the selected class will appear here."
                    : "Choose a school first, then a class, to see its students."}
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="cm-footer">
            <span className="cm-count">
              {memberIds.length > 0
                ? `${memberIds.length} student${
                    memberIds.length === 1 ? "" : "s"
                  } selected`
                : "No members selected yet"}
            </span>
            <div className="cm-footer-actions">
              <button
                type="button"
                className="cm-btn cm-btn--ghost"
                onClick={onHide}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cm-btn cm-btn--primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader size={16} className="animate-spin-custom" />
                    Saving…
                  </>
                ) : editing ? (
                  "Save Changes"
                ) : (
                  "Create Cohort"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
};

export default AdminCreateCohortModal;
